/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.reminders

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.model.reminder.PendingReminder
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.feature.reminders.generated.resources.Res
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_card_summary_many
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_card_summary_one
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_empty_state
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_button
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_send_button
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_title

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RemindersScreen(
    onBackClick: () -> Unit,
    modifier: Modifier = Modifier,
    filterPatientId: String? = null,
    viewModel: RemindersViewModel = koinViewModel(),
    currencyFormatter: CurrencyFormatter = koinInject(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()
    val snackbar = remember { SnackbarHostState() }
    val snackbarMsg = state.snackbar?.asString()

    LaunchedEffect(filterPatientId) {
        viewModel.trySendAction(RemindersAction.SetFilter(filterPatientId))
    }
    LaunchedEffect(snackbarMsg) {
        val msg = snackbarMsg ?: return@LaunchedEffect
        snackbar.showSnackbar(msg)
        viewModel.trySendAction(RemindersAction.DismissSnackbar)
    }

    val visible = remember(state.pending, state.filterPatientId) {
        state.filterPatientId?.let { id -> state.pending.filter { it.patientId == id } }
            ?: state.pending
    }

    Scaffold(
        modifier = modifier,
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.feature_reminders_title)) },
                navigationIcon = {
                    IconButton(onClick = onBackClick) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null)
                    }
                },
            )
        },
        snackbarHost = { SnackbarHost(snackbar) },
    ) { padding ->
        when {
            state.isLoading -> Box(
                Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentAlignment = Alignment.Center,
            ) { CircularProgressIndicator() }

            visible.isEmpty() -> Box(
                Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentAlignment = Alignment.Center,
            ) { Text(stringResource(Res.string.feature_reminders_empty_state)) }

            else -> LazyColumn(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                items(visible, key = { it.patientId }) { p ->
                    PatientReminderCard(
                        patient = p,
                        currencyFormatter = currencyFormatter,
                        sendEnabled = state.isTemplateReady,
                        markingPaid = state.markingPaid,
                        onSendClick = {
                            viewModel.trySendAction(RemindersAction.SendClicked(p.patientId))
                        },
                        onMarkAsPaidClick = {
                            viewModel.trySendAction(RemindersAction.MarkAsPaidClicked(p.patientId))
                        },
                    )
                }
            }
        }

        state.confirmDialog?.let { dialog ->
            ConfirmReminderDialog(
                dialog = dialog,
                isSending = state.sending,
                onConfirm = { viewModel.trySendAction(RemindersAction.ConfirmSend(dialog)) },
                onDismiss = { viewModel.trySendAction(RemindersAction.DismissDialog) },
            )
        }

        state.markPaidDialog?.let { dialog ->
            MarkAsPaidDialog(
                dialog = dialog,
                isMarking = state.markingPaid,
                onToggleSession = { sessionId ->
                    viewModel.trySendAction(RemindersAction.MarkPaidSessionToggled(sessionId))
                },
                onMethodChange = { method ->
                    viewModel.trySendAction(RemindersAction.MarkPaidMethodChanged(method))
                },
                onConfirm = { viewModel.trySendAction(RemindersAction.ConfirmMarkPaid) },
                onDismiss = { viewModel.trySendAction(RemindersAction.DismissMarkPaidDialog) },
            )
        }
    }
}

@Composable
private fun PatientReminderCard(
    patient: PendingReminder,
    currencyFormatter: CurrencyFormatter,
    sendEnabled: Boolean,
    markingPaid: Boolean,
    onSendClick: () -> Unit,
    onMarkAsPaidClick: () -> Unit,
) {
    val amount = currencyFormatter.format(patient.totalOwed)
    val summary = if (patient.dueSessions.size == 1) {
        stringResource(Res.string.feature_reminders_card_summary_one, amount)
    } else {
        stringResource(
            Res.string.feature_reminders_card_summary_many,
            patient.dueSessions.size,
            amount,
        )
    }
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(patient.patientName, style = MaterialTheme.typography.titleMedium)
            Text(patient.patientPhone, style = MaterialTheme.typography.bodySmall)
            Text(summary, style = MaterialTheme.typography.bodyMedium)
            Button(
                onClick = onSendClick,
                enabled = sendEnabled,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(stringResource(Res.string.feature_reminders_send_button))
            }
            OutlinedButton(
                onClick = onMarkAsPaidClick,
                enabled = !markingPaid,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(stringResource(Res.string.feature_reminders_mark_paid_button))
            }
        }
    }
}
